import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  type BoxProps,
  Flex,
  Spinner,
  Text,
  useColorModeValue,
} from "@chakra-ui/react"
import type { ReactNode } from "react"

type WmsTableListCardProps = {
  children?: ReactNode
  emptyMessage?: string
  error?: unknown
  errorMessage: string
  footer?: ReactNode
  isEmpty?: boolean
  isFetching?: boolean
  isLoading?: boolean
  minEmptyHeight?: BoxProps["minH"]
}

export function WmsTableListCard({
  children,
  emptyMessage = "Brak danych",
  error,
  errorMessage,
  footer,
  isEmpty = false,
  isFetching = false,
  isLoading = false,
  minEmptyHeight = { base: "50vh", lg: 0 },
}: WmsTableListCardProps) {
  const cardBg = useColorModeValue("white", "gray.800")
  const cardBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const mutedText = useColorModeValue("gray.500", "gray.400")

  if (isLoading) {
    return (
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={cardBorder}
        borderRadius="xl"
        minH={minEmptyHeight}
        h={{ base: "50vh", lg: "full" }}
      >
        <Flex justify="center" align="center" w="full" h="full">
          <Spinner size="xl" color="main.accent" />
        </Flex>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        {errorMessage}: {(error as Error)?.message ?? "Błąd"}
      </Alert>
    )
  }

  if (isEmpty) {
    return (
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={cardBorder}
        borderRadius="xl"
        minH={minEmptyHeight}
        h={{ base: "50vh", lg: "full" }}
      >
        <Flex justify="center" align="center" w="full" h="full" px={4}>
          <Text color={mutedText} textAlign="center">
            {emptyMessage}
          </Text>
        </Flex>
      </Box>
    )
  }

  return (
    <Box
      bg={cardBg}
      borderWidth="1px"
      borderColor={cardBorder}
      borderRadius="xl"
      overflow="hidden"
      position="relative"
      display="flex"
      flexDirection="column"
      h="full"
      minH={0}
    >
      {isFetching ? (
        <Badge
          colorScheme="blue"
          position="absolute"
          top={3}
          right={4}
          zIndex={2}
        >
          Aktualizacja...
        </Badge>
      ) : null}
      {children}
      {footer}
    </Box>
  )
}
